import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Pagination,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {toast} from 'react-toastify';
import ApiUtils from '../../utils/apiUtils';
import {FieldLabel, autocompleteSx} from './partnerFormField';

const GRANTS_PER_PAGE = 10;
const MIN_SEARCH_LENGTH = 2;

/**
 * Which companies a partner may read.
 *
 * Two halves, both paged, because neither list is small: a server-side picker so
 * the browser never receives the whole company table, and a paged table of what
 * is already assigned.
 */
const PartnerCompaniesPanel = (props) => {
  const [grants, setGrants] = useState([]);
  const [page, setPage] = useState(1);
  const [pagesCount, setPagesCount] = useState(0);
  const [effectiveCount, setEffectiveCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const [options, setOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [hasMoreOptions, setHasMoreOptions] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [includeDescendants, setIncludeDescendants] = useState(true);
  const [adding, setAdding] = useState(false);

  const loadGrants = useCallback(async (targetPage) => {
    setLoading(true);
    const result = await ApiUtils.makeApiCall(
      'GET',
      `partners/${props.partnerId}/grants?page=${targetPage}&limit=${GRANTS_PER_PAGE}`
    );
    setLoading(false);

    if (!result || result.status === 'error') {
      toast(result?.message || 'Could not load assigned companies.', {type: 'error'});
      return;
    }

    setGrants(result.data?.grants ?? []);
    setPagesCount(result.data?.pagesCount ?? 0);
    setEffectiveCount(result.data?.effectiveCompanyCount ?? 0);
  }, [props.partnerId]);

  useEffect(() => {
    if (props.partnerId) loadGrants(page);
  }, [props.partnerId, page, loadGrants]);

  // Debounced so a fast typist does not fire a request per keystroke.
  useEffect(() => {
    if (searchTerm.trim().length < MIN_SEARCH_LENGTH) {
      setOptions([]);
      setHasMoreOptions(false);
      return undefined;
    }

    let cancelled = false;
    setSearching(true);

    const timer = setTimeout(async () => {
      const result = await ApiUtils.makeApiCall(
        'GET',
        `partners/company-search?q=${encodeURIComponent(searchTerm.trim())}&limit=25`
      );

      if (cancelled) return;

      setSearching(false);
      if (!result || result.status === 'error') {
        setOptions([]);
        return;
      }
      setOptions(result.data?.companies ?? []);
      setHasMoreOptions(!!result.data?.hasMore);
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchTerm]);

  const addCompany = async () => {
    if (!selectedCompany) return;

    setAdding(true);
    const result = await ApiUtils.makeApiCall(
      'POST',
      `partners/${props.partnerId}/grants`,
      JSON.stringify({
        companyId: selectedCompany.id,
        includeDescendants,
      })
    );
    setAdding(false);

    if (!result || result.status === 'error') {
      toast(result?.message || 'Could not assign the company.', {type: 'error'});
      return;
    }

    toast(`${selectedCompany.name} assigned.`, {type: 'success'});
    setSelectedCompany(null);
    setSearchTerm('');
    setPage(1);
    await loadGrants(1);
    if (props.onChange) props.onChange();
  };

  const revoke = async (grant) => {
    const result = await ApiUtils.makeApiCall(
      'POST',
      `partners/${props.partnerId}/grants/${grant.id}/revoke`
    );

    if (!result || result.status === 'error') {
      toast(result?.message || 'Could not revoke access.', {type: 'error'});
      return;
    }

    toast(`Access to ${grant.companyName || 'the company'} revoked.`, {type: 'success'});
    await loadGrants(page);
    if (props.onChange) props.onChange();
  };

  return (
    <Box>
      <Typography variant={'subtitle2'} sx={{mb: 1}}>Assign a company</Typography>

      <Box display={'flex'} gap={2} alignItems={'flex-end'} flexWrap={'wrap'} mb={1}>
        <Box sx={{minWidth: 320, flexGrow: 1}}>
          <FieldLabel label={'Search companies'}/>
          <Autocomplete
            sx={autocompleteSx}
            size={'small'}
            options={options}
            value={selectedCompany}
            onChange={(e, value) => setSelectedCompany(value)}
            inputValue={searchTerm}
            onInputChange={(e, value) => setSearchTerm(value)}
            getOptionLabel={(option) => option.name || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={searching}
            // The server decides what matches; filtering again here would hide
            // results it deliberately returned.
            filterOptions={(x) => x}
            noOptionsText={
              searchTerm.trim().length < MIN_SEARCH_LENGTH
                ? 'Type at least 2 characters'
                : 'No companies match'
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={'Type at least 2 characters'}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searching ? <CircularProgress size={16}/> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={includeDescendants}
              onChange={(e) => setIncludeDescendants(e.target.checked)}
            />
          }
          label={'Include sub-accounts'}
        />

        <Button
          variant={'contained'}
          onClick={addCompany}
          disabled={!selectedCompany || adding}
        >
          Assign
        </Button>
      </Box>

      {hasMoreOptions && (
        <Typography variant={'caption'} sx={{color: 'text.secondary'}}>
          More companies match than are shown &mdash; keep typing to narrow.
        </Typography>
      )}

      <Box mt={3} mb={1} display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
        <Typography variant={'subtitle2'}>Assigned companies</Typography>
        {effectiveCount > 0 && (
          <Chip
            size={'small'}
            color={'primary'}
            variant={'outlined'}
            label={`Reads ${effectiveCount} ${effectiveCount === 1 ? 'company' : 'companies'}`}
          />
        )}
      </Box>

      {/*
        Selecting one enterprise with sub-accounts can resolve to dozens of
        companies. Showing only the row count would let someone authorize a whole
        subtree without ever seeing its size.
      */}
      {effectiveCount > grants.length && (
        <Alert severity={'info'} sx={{mb: 2}}>
          Sub-accounts are included, so this partner can read{' '}
          <strong>{effectiveCount} companies</strong> from{' '}
          {grants.length} {grants.length === 1 ? 'assignment' : 'assignments'}.
        </Alert>
      )}

      {loading && <CircularProgress size={20}/>}

      {!loading && grants.length === 0 && (
        <Alert severity={'warning'}>
          No companies assigned. This partner can authenticate but will read
          nothing.
        </Alert>
      )}

      {!loading && grants.length > 0 && (
        <>
          <Table size={'small'}>
            <TableHead>
              <TableRow>
                <TableCell>Company</TableCell>
                <TableCell>Sub-accounts</TableCell>
                <TableCell>Reach</TableCell>
                <TableCell>Assigned by</TableCell>
                <TableCell>Assigned</TableCell>
                <TableCell align={'right'}/>
              </TableRow>
            </TableHead>
            <TableBody>
              {grants.map((grant) => (
                <TableRow key={grant.id}>
                  <TableCell>{grant.companyName || grant.companyId}</TableCell>
                  <TableCell>{grant.includeDescendants ? 'Included' : 'Excluded'}</TableCell>
                  <TableCell>{grant.effectiveCompanyCount ?? 1}</TableCell>
                  <TableCell>{grant.grantedByName || '—'}</TableCell>
                  <TableCell>
                    {grant.grantedAt ? new Date(grant.grantedAt).toLocaleDateString() : '—'}
                  </TableCell>
                  <TableCell align={'right'}>
                    <Tooltip title={'Revoke access'}>
                      <IconButton size={'small'} onClick={() => revoke(grant)}>
                        <DeleteOutlineIcon fontSize={'small'}/>
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {pagesCount > 1 && (
            <Box mt={2} display={'flex'} justifyContent={'center'}>
              <Pagination
                count={pagesCount}
                page={page}
                onChange={(e, value) => setPage(value)}
                size={'small'}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

PartnerCompaniesPanel.propTypes = {
  partnerId: PropTypes.string.isRequired,
  onChange: PropTypes.func,
};

PartnerCompaniesPanel.defaultProps = {
  onChange: undefined,
};

export default PartnerCompaniesPanel;
